import { times } from 'lodash';
import Formsy from 'lib/Formsy';
import RecipientParser from 'lib/RecipientParser';
import { DirectAddErrorType } from 'globalTypes';
import { isFeatureEnabled } from 'lib/FeatureFlag';
import pluralize from 'pluralize';

const ERROR_LEVELS = {
  warning: 'warning' as const,
  error: 'error' as const,
};

export type InputField = {
  value: string;
  errorLevel?: keyof typeof ERROR_LEVELS | null;
  errorMessage?: string | null;
};

export type UserRow = {
  name: InputField;
  firstName: InputField;
  lastName: InputField;
  primaryDevice: InputField;
  secondaryDevice: InputField;
  preferredLanguage: InputField;
  userValid: boolean;
};

type DirectAddServerError = {
  errorType: DirectAddErrorType;
  message: string;
};

export type ServerUserError = {
  address: string;
  errorsWithTypes: DirectAddServerError[];
};

type errorUserCollectionGeneratorOutput = {
  remainingErrors: ServerUserError[];
  userCollection: UserRow[];
};

type StudentRole = 'student';
type StaffRole = 'staff';
type TeacherRole = 'teacher';
type ParentRole = 'parent';
type EmployeeRole = 'employee';

export type UserRole = StudentRole | StaffRole | TeacherRole | ParentRole | EmployeeRole;

type StudentsTabName = 'students';
type StaffTabName = 'staff';
type TeachersTabName = 'teachers';
type ParentsTabName = 'parents';
type EmployeesTabName = 'employees';
type NoRoleTabName = 'noRole';

export type UserRoleTabName =
  | StudentsTabName
  | StaffTabName
  | TeachersTabName
  | ParentsTabName
  | EmployeesTabName
  | NoRoleTabName;
export const DIRECT_ADD_USER_ROLES: UserRole[] = [
  'student',
  'parent',
  'teacher',
  'employee',
  'staff',
];

export const ERROR_MESSAGES = {
  nameWarning:
    'If no name is entered, participants will be asked for their names before they’re added to your class',
  phoneInvalidError: 'Invalid phone number',
  emailRequiredError: 'Valid email required',
  deviceRequiredError: 'Valid phone number or email address required',
  deviceInvalidError: 'Invalid email address or phone number',
};

type State = {
  teachers: Array<UserRow>;
  students: Array<UserRow>;
  parents: Array<UserRow>;
  staff: Array<UserRow>;
  employees: Array<UserRow>;
  noRole: Array<UserRow>;
  maxRows: number;
  containsData: boolean;
  activeTab: UserRoleTabName;
};

export const MINIMUM_ROWS = 5;
export const MAXIMUM_ROWS = {
  group: 150,
  organization: 150,
};

export const initialState: State = {
  teachers: buildInitialState(),
  students: buildInitialState(),
  parents: buildInitialState(),
  staff: buildInitialState(),
  employees: buildInitialState(),
  noRole: buildInitialState(),
  maxRows: MAXIMUM_ROWS.group,
  containsData: false,
  activeTab: 'noRole',
};
/* ======= */
/* REDUCER */
/* ======= */

export default function reducer(state: State = initialState, action: any): State {
  switch (action.type) {
    case 'directAddForm/HANDLE_INPUT_CHANGE': {
      const collection = pluralize(action.role);
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const currentUserCollection = state[collection];
      const updatedUserCollection = insertIntoUserCollection(
        currentUserCollection,
        action.user,
        action.key
      );

      const resizedCollection = resizeCollection(updatedUserCollection, state.maxRows);
      const stateWithNewRows = { ...state, [collection]: resizedCollection };

      return { ...updatedStateWithEmptyCheck(stateWithNewRows) };
    }
    case 'directAddForm/HANDLE_PASTE': {
      const collection = pluralize(action.role);
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const currentUserCollection = state[collection];
      const updatedUserCollection = insertIntoUserCollectionFromPaste(
        currentUserCollection,
        action.startKey,
        action.rows,
        action.emailRequired
      );

      const resizedCollection = resizeCollection(updatedUserCollection, state.maxRows);
      const stateWithNewRows = { ...state, [collection]: resizedCollection };

      return { ...updatedStateWithEmptyCheck(stateWithNewRows) };
    }
    case 'directAddForm/HANDLE_SERVER_ERRORS': {
      const { errors } = action;
      const stateWithErrors = updatedStateWithServerErrors(errors, state);

      return { ...updatedStateWithEmptyCheck(stateWithErrors) };
    }
    case 'directAddForm/SET_MAX_ROWS_FOR_ENTITY': {
      return { ...state, maxRows: action.maxRows };
    }
    case 'directAddForm/REINITIALIZE': {
      return { ...initialState };
    }
    case 'directAddForm/HANDLE_CLEAR_TABLE': {
      return { ...state, [action.role]: buildInitialState() };
    }
    case 'directAddForm/HANDLE_ACTIVE_TAB_CHANGE': {
      return { ...state, activeTab: action.role };
    }
    default:
      return state;
  }
}

/* ======= */
/* ACTIONS */
/* ======= */

export type ReinitializeAction = {
  type: 'directAddForm/REINITIALIZE';
};

export function reinitialize(): ReinitializeAction {
  return { type: 'directAddForm/REINITIALIZE' };
}

export type HandleInputChange = {
  type: 'directAddForm/HANDLE_INPUT_CHANGE';
  key: number;
  role: string;
  user: UserRow;
};

export function handleInputChange(key: number, role: string, user: UserRow): HandleInputChange {
  return {
    type: 'directAddForm/HANDLE_INPUT_CHANGE',
    key,
    role,
    user,
  };
}

export type HandlePaste = {
  type: 'directAddForm/HANDLE_PASTE';
  startKey: number;
  role: string;
  rows: string[][];
};

export function handlePaste(
  startKey: number,
  role: string,
  rows: string[][],
  emailRequired: boolean
): HandlePaste {
  return {
    type: 'directAddForm/HANDLE_PASTE',
    startKey,
    role,
    rows,
    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ type: "directAddForm/HANDLE_PASTE"; startK... Remove this comment to see the full error message
    emailRequired,
  };
}

export type SetMaxRowsForEntity = {
  type: 'directAddForm/SET_MAX_ROWS_FOR_ENTITY';
  maxRows: number;
};

export function setMaxRowsForEntity(entity: 'group' | 'organization'): SetMaxRowsForEntity {
  const maxRows = MAXIMUM_ROWS[entity];
  return {
    type: 'directAddForm/SET_MAX_ROWS_FOR_ENTITY',
    maxRows,
  };
}

export type HandleServerErrors = {
  type: 'directAddForm/HANDLE_SERVER_ERRORS';
  errors: ServerUserError[];
};

export function handleServerErrors(errors: ServerUserError[]): HandleServerErrors {
  return {
    type: 'directAddForm/HANDLE_SERVER_ERRORS',
    errors,
  };
}

export type HandleClearTable = {
  type: 'directAddForm/HANDLE_CLEAR_TABLE';
  role: string;
};

export function handleClearTable(role: string): HandleClearTable {
  return {
    type: 'directAddForm/HANDLE_CLEAR_TABLE',
    role,
  };
}

export type HandleActiveTabChange = {
  type: 'directAddForm/HANDLE_ACTIVE_TAB_CHANGE';
  role: string;
};

export function handleActiveTabChange(role: string): HandleActiveTabChange {
  return {
    type: 'directAddForm/HANDLE_ACTIVE_TAB_CHANGE',
    role,
  };
}

/* ======= */
/* HELPERS */
/* ======= */

export const studentRole: StudentRole = 'student';
export const staffRole: StaffRole = 'staff';
export const teacherRole: TeacherRole = 'teacher';
export const parentRole: ParentRole = 'parent';
export const employeeRole: EmployeeRole = 'employee';

export function tabNameFor(role: UserRole): UserRoleTabName {
  const studentsTabName: StudentsTabName = 'students';
  const staffTabName: StaffTabName = 'staff';
  const teachersTabName: TeachersTabName = 'teachers';
  const parentsTabName: ParentsTabName = 'parents';
  const employeesTabName: EmployeesTabName = 'employees';

  switch (role) {
    case studentRole:
      return studentsTabName;
    case staffRole:
      return staffTabName;
    case teacherRole:
      return teachersTabName;
    case parentRole:
      return parentsTabName;
    case employeeRole:
      return employeesTabName;
  }
}

export function userRowEmpty(user: UserRow): boolean {
  const nameCompleted =
    user.name.value.length === 0 &&
    user.firstName.value.length === 0 &&
    user.lastName.value.length === 0;
  return (
    nameCompleted &&
    user.primaryDevice.value.length === 0 &&
    user.secondaryDevice.value.length === 0 &&
    user.preferredLanguage.value.length === 0
  );
}

function emptyUserRow(): UserRow {
  return {
    name: {
      value: '',
    },
    firstName: {
      value: '',
    },
    lastName: {
      value: '',
    },
    primaryDevice: {
      value: '',
    },
    secondaryDevice: {
      value: '',
    },
    preferredLanguage: {
      value: '',
    },
    userValid: true,
  };
}

function buildInitialState(): Array<UserRow> {
  return times(MINIMUM_ROWS, () => emptyUserRow());
}

function insertIntoUserCollection(
  currentUserCollection: Array<UserRow>,
  newUser: UserRow,
  index: number
): Array<UserRow> {
  const updatedUserCollection = [...currentUserCollection];
  updatedUserCollection[index] = newUser;

  return updatedUserCollection;
}

function resizeCollection(collection: UserRow[], maxRows: number): UserRow[] {
  const trimmedRows = trimRows(collection, maxRows);
  return padRows(trimmedRows, maxRows);
}

// Remove all the empty rows from the end
function trimRows(rows: Array<UserRow>, maxRows: number): Array<UserRow> {
  const lastIndex = rows.length - 1;
  for (let i = lastIndex; i >= MINIMUM_ROWS - 1; i--) {
    if (userRowEmpty(rows[i])) {
      rows.pop();
    } else {
      break;
    }
  }
  return rows.slice(0, maxRows);
}

function padRows(rows: Array<UserRow>, maxRows: number): Array<UserRow> {
  const rowsUnderMinimum = MINIMUM_ROWS - rows.length;
  const collectionWithinMaxLimit = rows.length < maxRows;

  if (rowsUnderMinimum > 0) {
    times(rowsUnderMinimum, () => rows.push(emptyUserRow()));
  } else if (collectionWithinMaxLimit) {
    rows.push(emptyUserRow());
  }
  return rows;
}

function insertIntoUserCollectionFromPaste(
  currentUserCollection: UserRow[],
  startingIndex: number,
  rows: string[][],
  emailRequired: boolean
): UserRow[] {
  const updatedUserCollection = [...currentUserCollection];

  const pastedUserRows = packagePastedDataIntoUserRows(rows, emailRequired);

  // Insert the users rows at the correct place in the collection
  updatedUserCollection.splice(startingIndex, pastedUserRows.length, ...pastedUserRows);

  return updatedUserCollection;
}

function packageUserRow(row: string[]) {
  const directAddEnhancements = isFeatureEnabled('direct-add-enhancements.web.feb-23');
  const directAddSplitNames = isFeatureEnabled('direct-add-split-name.web.mar-23');

  let nameValue = '';
  let firstNameValue = '';
  let lastNameValue = '';
  let primaryDeviceValue;
  let secondaryDeviceValue;
  let preferredLanguageValue = '';

  if (directAddSplitNames) {
    firstNameValue = row[0].trim();
    lastNameValue = row[1].trim();
    primaryDeviceValue = (row[2] || '').trim();
    secondaryDeviceValue = (row[3] || '').trim();
    if (directAddEnhancements) {
      preferredLanguageValue = (row[4] || '').trim();
    }
  } else {
    nameValue = row[0].trim();
    primaryDeviceValue = (row[1] || '').trim();
    secondaryDeviceValue = (row[2] || '').trim();
    if (directAddEnhancements) {
      preferredLanguageValue = (row[3] || '').trim();
    }
  }

  return {
    name: packageInputField(nameValue),
    firstName: packageInputField(firstNameValue),
    lastName: packageInputField(lastNameValue),
    primaryDevice: packageInputField(primaryDeviceValue),
    secondaryDevice: packageInputField(secondaryDeviceValue),
    preferredLanguage: packageInputField(preferredLanguageValue),
    userValid: false,
  };
}

function packagePastedDataIntoUserRows(rows: string[][], emailRequired: boolean): UserRow[] {
  return rows.map((row) => {
    const inputtedRow = packageUserRow(row);

    return addValidationErrors(inputtedRow, emailRequired);
  });
}

export function addValidationErrors(
  userRow: UserRow,
  emailRequired: boolean,
  primaryDeviceInFocus: boolean = false
): UserRow {
  const name = userRow.name.value;
  const firstName = userRow.firstName.value;
  const lastName = userRow.lastName.value;
  const primaryDevice = userRow.primaryDevice.value;
  const secondaryDevice = userRow.secondaryDevice.value;
  const preferredLanguage = userRow.preferredLanguage.value;

  let firstNameWarning = null;
  if (isFeatureEnabled('direct-add-enhancements.web.feb-23')) {
    firstNameWarning = getNameWarningMessage(firstName, primaryDevice);
  }

  const nameWarning = getNameWarningMessage(name, primaryDevice);
  // Don't show an error in the primary device field if cursor is there
  const primaryDeviceError =
    (firstName || lastName || name || primaryDevice || secondaryDevice) && !primaryDeviceInFocus
      ? getPrimaryDeviceError(primaryDevice, emailRequired)
      : null;

  const secondaryDeviceError = secondaryDevice
    ? getSecondaryDeviceError(secondaryDevice, emailRequired)
    : null;

  const userValid = !(primaryDeviceError || secondaryDeviceError);

  return {
    ...userRow,
    name: packageInputField(name, nameWarning, nameWarning ? ERROR_LEVELS.warning : null),
    firstName: packageInputField(
      firstName,
      firstNameWarning,
      firstNameWarning ? ERROR_LEVELS.warning : null
    ),
    lastName: packageInputField(lastName, null, null),
    primaryDevice: packageInputField(
      primaryDevice,
      primaryDeviceError,
      primaryDeviceError ? ERROR_LEVELS.error : null
    ),
    secondaryDevice: packageInputField(
      secondaryDevice,
      secondaryDeviceError,
      secondaryDeviceError ? ERROR_LEVELS.error : null
    ),
    preferredLanguage: packageInputField(preferredLanguage, null, null),
    userValid,
  };
}

function packageInputField(
  value: string,
  errorMessage?: string | null,
  errorLevel?: keyof typeof ERROR_LEVELS | null
): InputField {
  return {
    value,
    errorMessage,
    errorLevel,
  };
}

function getNameWarningMessage(name: string, primaryDevice: string) {
  return !name && primaryDevice ? ERROR_MESSAGES.nameWarning : null;
}

function getPrimaryDeviceError(device: string, emailRequired: boolean): string | undefined | null {
  if (emailRequired) {
    return isEmailValid(device) ? null : ERROR_MESSAGES.emailRequiredError;
  } else {
    return isDeviceValid(device) ? null : ERROR_MESSAGES.deviceRequiredError;
  }
}

function getSecondaryDeviceError(
  device: string,
  emailRequired: boolean
): string | undefined | null {
  return isDeviceValid(device) ? null : ERROR_MESSAGES.deviceInvalidError;
}

function isDeviceValid(device: string) {
  return isEmailValid(device) || isPhoneValid(device);
}

function isEmailValid(email: string) {
  return Formsy.customValidators.isEmailWithSpaces({}, email);
}

function isPhoneValid(phone: string) {
  return Formsy.customValidators.couldPlausiblyBeANorthAmericanPhoneNumber({}, phone);
}

function someRowFilled(
  students: UserRow[],
  parents: UserRow[],
  teachers: UserRow[],
  staff: UserRow[],
  employees: UserRow[]
): boolean {
  return (
    students.some((userRow) => !userRowEmpty(userRow)) ||
    parents.some((userRow) => !userRowEmpty(userRow)) ||
    teachers.some((userRow) => !userRowEmpty(userRow)) ||
    staff.some((userRow) => !userRowEmpty(userRow)) ||
    employees.some((userRow) => !userRowEmpty(userRow))
  );
}

function updatedStateWithEmptyCheck(state: State): State {
  const containsData = someRowFilled(
    state.students,
    state.parents,
    state.teachers,
    state.staff,
    state.employees
  );

  return { ...state, containsData };
}

function updatedStateWithServerErrors(errors: ServerUserError[], state: State): State {
  const studentErrorCollection = generateErrorUserCollection(errors, state.students);
  const parentErrorCollection = generateErrorUserCollection(
    studentErrorCollection.remainingErrors,
    state.parents
  );
  const teacherErrorCollection = generateErrorUserCollection(
    parentErrorCollection.remainingErrors,
    state.teachers
  );
  const staffErrorCollection = generateErrorUserCollection(
    teacherErrorCollection.remainingErrors,
    state.staff
  );
  const employeeErrorCollection = generateErrorUserCollection(
    staffErrorCollection.remainingErrors,
    state.employees
  );

  if (employeeErrorCollection.remainingErrors.length > 0) {
    console.error('directAddForm not all server errors matched');
  }

  const students = resizeCollection(studentErrorCollection.userCollection, state.maxRows);
  const parents = resizeCollection(parentErrorCollection.userCollection, state.maxRows);
  const teachers = resizeCollection(teacherErrorCollection.userCollection, state.maxRows);
  const staff = resizeCollection(staffErrorCollection.userCollection, state.maxRows);
  const employees = resizeCollection(employeeErrorCollection.userCollection, state.maxRows);

  return { ...state, students, parents, teachers, staff, employees };
}

export function generateErrorUserCollection(
  errors: ServerUserError[],
  collection: UserRow[]
): errorUserCollectionGeneratorOutput {
  return errors.reduce(
    ({ remainingErrors, userCollection }, error) => {
      const matchingUser = collection.find((user) => {
        return (
          RecipientParser.addressesMatch(error.address, user.primaryDevice.value) ||
          RecipientParser.addressesMatch(error.address, user.secondaryDevice.value)
        );
      });

      if (matchingUser) {
        const matchedByPrimaryDevice = RecipientParser.addressesMatch(
          error.address,
          matchingUser.primaryDevice.value
        );
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'UserRow' is not assignable to pa... Remove this comment to see the full error message
        userCollection.push(buildErrorUser(matchingUser, error, matchedByPrimaryDevice));

        // Remove error from array
        const errorIndex = remainingErrors.indexOf(error);
        remainingErrors.splice(errorIndex, 1);
      }

      return { remainingErrors, userCollection };
    },
    { remainingErrors: [...errors], userCollection: [] }
  );
}

function fieldNameFromError(error: DirectAddServerError, matchedByPrimaryDevice: boolean) {
  const errorTypeFieldNameMap = new Map(
    Object.entries({
      [DirectAddErrorType.invalid_preferred_language_code]: 'preferredLanguage' as const,
      [DirectAddErrorType.profane_name]: 'name' as const,
      [DirectAddErrorType.contains_url]: 'name' as const,
    })
  );
  const defaultFieldName = matchedByPrimaryDevice ? 'primaryDevice' : 'secondaryDevice';

  return errorTypeFieldNameMap.get(error.errorType) ?? defaultFieldName;
}

function buildErrorUser(
  user: UserRow,
  error: ServerUserError,
  matchedByPrimaryDevice: boolean
): UserRow {
  let fieldsWithErrors: Partial<UserRow> = {};
  error.errorsWithTypes.forEach((directAddServerError) => {
    const fieldName = fieldNameFromError(directAddServerError, matchedByPrimaryDevice);

    if (fieldName === 'name' && isFeatureEnabled('direct-add-split-name.web.mar-23')) {
      // TODO this is until the split name feature is implemented in the backend
      // right now, as it is not implemented and the backend receives a full name, if there is an error in the name
      // we don't know if it is first or last name. So we put the error in both fields
      fieldsWithErrors['firstName'] = {
        ...user['firstName'],
        errorLevel: 'error' as const,
        errorMessage: directAddServerError.message,
      };
      fieldsWithErrors['lastName'] = {
        ...user['lastName'],
        errorLevel: 'error' as const,
        errorMessage: directAddServerError.message,
      };
    } else {
      fieldsWithErrors[fieldName] = {
        ...user[fieldName],
        errorLevel: 'error' as const,
        errorMessage: directAddServerError.message,
      };
    }
  });

  return {
    ...user,
    ...fieldsWithErrors,
    userValid: false,
  };
}
